import type { TenantNotification } from "../app/types";
import { notificationsViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";

type NotificationsViewProps = {
  notifications: TenantNotification[];
  language: AppLanguage;
  isLoading?: boolean;
  onDismissNotification: (notificationId: string) => void;
};

const severityStyles: Record<TenantNotification["severity"], { badge: string; dot: string }> = {
  info: {
    badge: "bg-[#eff8ff] text-[#175cd3]",
    dot: "bg-[#0ea5e9]",
  },
  warning: {
    badge: "bg-[#fff7ed] text-[#c4320a]",
    dot: "bg-[#f59e0b]",
  },
  critical: {
    badge: "bg-[#fef3f2] text-[#b42318]",
    dot: "bg-[#dc2626]",
  },
  success: {
    badge: "bg-[#ecfdf3] text-[#027a48]",
    dot: "bg-[#16a34a]",
  },
};

function formatDateTime(value: string | null, language: AppLanguage) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(getIntlLocale(language), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getWindowLabel(notification: TenantNotification, language: AppLanguage, copy: typeof notificationsViewCopy[AppLanguage]) {
  const startsAt = formatDateTime(notification.startsAt, language);
  const endsAt = formatDateTime(notification.endsAt, language);

  if (startsAt && endsAt) {
    return `${startsAt} → ${endsAt}`;
  }

  if (startsAt) {
    return copy.schedule.starts(startsAt);
  }

  if (endsAt) {
    return copy.schedule.ends(endsAt);
  }

  return copy.schedule.noWindow;
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function NotificationStatCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#98a2b3]">{label}</p>
      <div className="mt-3 min-h-[2.5rem]">
        {isLoading ? <CountSpinner /> : <p className="font-headline text-4xl font-extrabold tracking-[-0.06em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]" role="status" aria-live="polite">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

export function NotificationsView({ notifications, language, isLoading = false, onDismissNotification }: NotificationsViewProps) {
  const copy = notificationsViewCopy[language];
  const activeNotifications = notifications.filter(notification => !notification.dismissedAt);
  const dismissedNotifications = notifications.filter(notification => notification.dismissedAt);

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">
            {copy.page.description}
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <NotificationStatCard label={copy.metrics.active} value={activeNotifications.length.toString().padStart(2, "0")} detail={copy.metrics.activeDescription} isLoading={isLoading} />
        <NotificationStatCard label={copy.metrics.archived} value={dismissedNotifications.length.toString().padStart(2, "0")} detail={copy.metrics.archivedDescription} isLoading={isLoading} />
      </div>

      <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.notices.title}</h2>
            <p className="mt-1 text-sm text-[#667085]">{copy.notices.description}</p>
          </div>
          <div className="rounded-2xl bg-[#f3f3f4] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#475467]">
            {copy.notices.total(notifications.length)}
          </div>
        </div>

        <div className="relative min-h-[160px]">
          {isLoading ? (
            <div className={`pointer-events-none absolute inset-x-0 z-[1] flex ${notifications.length === 0 ? "inset-y-0 items-center justify-center bg-white/75 backdrop-blur-[1px]" : "bottom-5 items-center justify-center px-5"}`}>
              <LoadingSpinnerBadge label={copy.notices.loading} />
            </div>
          ) : null}

          {notifications.length === 0 ? (
            isLoading ? null : (
              <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] p-8 text-center">
                <p className="text-sm font-semibold text-[#101828]">{copy.notices.emptyTitle}</p>
                <p className="mt-2 text-sm text-[#667085]">{copy.notices.emptyDescription}</p>
              </div>
            )
          ) : (
            <div className="space-y-3">
              {notifications.map(notification => {
                const styles = severityStyles[notification.severity];

                return (
                  <article key={notification.id} className="rounded-[24px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5 transition hover:-translate-y-[1px] hover:border-[rgba(0,105,73,0.2)] hover:shadow-[0_18px_44px_rgba(15,23,42,0.08)]">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex h-2.5 w-2.5 rounded-full ${styles.dot}`} aria-hidden="true" />
                          <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${styles.badge}`}>{copy.kinds[notification.kind]}</span>
                          {notification.dismissedAt ? <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#667085]">{copy.notices.dismissed}</span> : null}
                        </div>
                        <h3 className="mt-3 text-base font-semibold text-[#101828]">{notification.title}</h3>
                        <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[#667085]">{notification.body}</p>
                        <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.24em] text-[#98a2b3]">{getWindowLabel(notification, language, copy)}</p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
                        {notification.statusUrl ? (
                          <a href={notification.statusUrl} target="_blank" rel="noreferrer" className="rounded-xl bg-[#f3f4f6] px-4 py-2 text-xs font-semibold text-[#475467] transition hover:bg-[#e4e7ec]">{copy.notices.statusPage}</a>
                        ) : null}
                        {notification.ctaUrl ? (
                          <a href={notification.ctaUrl} target="_blank" rel="noreferrer" className="brand-primary-button rounded-xl px-4 py-2 text-xs font-semibold transition-all">{notification.ctaLabel || copy.notices.learnMore}</a>
                        ) : null}
                        {!notification.dismissedAt ? (
                          <button type="button" onClick={() => onDismissNotification(notification.id)} className="rounded-xl bg-[#f3f4f6] px-4 py-2 text-xs font-semibold text-[#475467] transition hover:bg-[#e4e7ec]">
                            {copy.notices.dismissAction}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}